import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Topbar from '../components/Topbar';
import axiosInstance from '../libs/axios';

const JobDetail = ({ token, onLogout }) => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await axiosInstance.get(`/jobs/${id}`);
        setJob(res.data.job);
      } catch (err) {
        console.error('Error fetching job:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  useEffect(() => {
    let interval;
    if (job && job.status === 'PENDING_AT_DATEV') {
      interval = setInterval(async () => {
        try {
          const res = await axiosInstance.get(`/jobs/${id}/datev/status`);
          setJob(res.data.job);
          if (res.data.job.status !== 'PENDING_AT_DATEV') {
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Error checking status:', err);
        }
      }, 25000);
    }
    return () => clearInterval(interval);
  }, [job, id]);

  useEffect(() => {
    let interval;
    if (job && ['PENDING_AT_DATEV', 'UPLOADING', 'COMPLETED'].includes(job.status)) {
      interval = setInterval(async () => {
        try {
          const res = await axiosInstance.get(`/jobs/${id}`);
          setJob(res.data.job);
        } catch (err) {
          console.error('Error refreshing job:', err);
        }
      }, 50000); // Refresh every 50 seconds for active jobs
    }
    return () => clearInterval(interval);
  }, [job, id]);

  if (!token) {
    return (
      <div className="landing">
        <div className="landing-inner">
          <h1>Job Detail</h1>
          <p>Please sign in to view job details.</p>
          <div className="actions">
            <Link className="btn-primary" to="/login">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div>Loading...</div>;
  if (!job) return <div>Job not found</div>;

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'COMPLETED': '#10b981', // green
      'READY': '#3b82f6', // blue
      'PROCESSING': '#f59e0b', // amber
      'UPLOADING': '#f59e0b', // amber
      'PENDING_AT_DATEV': '#8b5cf6', // purple
      'FAILED': '#ef4444', // red
      'ERROR': '#ef4444', // red
      'CANCELLED': '#6b7280', // gray
    };
    return statusColors[status] || '#6b7280';
  };

  // Helper function to get status description
  const getStatusDescription = (status) => {
    const descriptions = {
      'COMPLETED': 'Job completed successfully. All files have been uploaded to DATEV.',
      'READY': 'Job is ready to be uploaded to DATEV. EXTF file has been generated.',
      'PROCESSING': 'Job is currently being processed. EXTF file is being generated.',
      'UPLOADING': 'Job is being uploaded to DATEV. Please wait...',
      'PENDING_AT_DATEV': 'Job has been submitted to DATEV and is pending processing.',
      'FAILED': 'Job processing failed. Please check the error details below.',
      'ERROR': 'An error occurred during job processing.',
      'CANCELLED': 'Job was cancelled.',
    };
    return descriptions[status] || 'Unknown status';
  };

  // Build files list from documents and fileMap
  const documents = job.documents || [];
  const fileMap = job.fileMap || [];
  
  // Create a map of GUID to Document for quick lookup
  const documentMap = {};
  documents.forEach(doc => {
    if (doc.documentId) {
      documentMap[doc.documentId] = doc;
    }
  });

  // Merge fileMap with Document records
  const files = fileMap.map(item => {
    const guid = item.guid || item.documentId;
    const doc = documentMap[guid];
    
    return {
      guid: guid,
      filename: doc?.fileName || item.filename || item.originalName || 'Unknown',
      status: doc?.status || item.status || 'unknown',
      errorCode: doc?.errorCode || item.errorCode,
      errorDescription: doc?.errorDescription || item.errorDescription,
      errorUri: doc?.errorUri || item.errorUri,
      uploadedAt: doc?.uploadedAt || item.uploadedAt,
    };
  });

  // If no files from fileMap, show documents directly
  const displayFiles = files.length > 0 ? files : documents.map(doc => ({
    guid: doc.documentId,
    filename: doc.fileName,
    status: doc.status,
    errorCode: doc.errorCode,
    errorDescription: doc.errorDescription,
    errorUri: doc.errorUri,
    uploadedAt: doc.uploadedAt,
  }));

  // Calculate actual uploaded count
  const actualUploadedCount = documents.filter(doc => doc.status === 'UPLOADED').length;
  const totalFiles = Math.max(displayFiles.length, job.totalCount || 0, documents.length);

  // Determine if job can be processed
  const canProcess = job.status === 'PROCESSING' || (!job.csvExtfUrl && job.status !== 'UPLOADING' && job.status !== 'PENDING_AT_DATEV' && job.status !== 'COMPLETED');
  const canUpload = job.status === 'READY' && job.csvExtfUrl;

  // Get DATEV submission info
  const datevSubmission = job.datevSubmission || {};
  const csvSubmission = datevSubmission.csv || {};
  const pdfSubmissions = datevSubmission.pdfs || [];

  const processJob = async () => {
    setActionError('');
    setProcessing(true);
    try {
      const res = await axiosInstance.post(`/jobs/${id}/process`);
      setJob(res.data.job);
    } catch (err) {
      setActionError(err?.response?.data?.detail || err.message);
    } finally {
      setProcessing(false);
    }
  };

  const uploadJob = async () => {
    setActionError('');
    setUploading(true);
    try {
      const res = await axiosInstance.post(`/jobs/${id}/datev/upload`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (res.data && res.data.job) {
        setJob(res.data.job);
      } else {
        const jobRes = await axiosInstance.get(`/jobs/${id}`);
        if (jobRes.data && jobRes.data.job) {
          setJob(jobRes.data.job);
        }
      }
    } catch (err) {
      const errorDetail = err?.response?.data?.detail || err.message || 'Failed to upload job to DATEV';
      setActionError(errorDetail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page">
      <Topbar onLogout={onLogout} />
      <main className="content">
        <h2>{job.name}</h2>
        
        {/* Status Card */}
        <div className="card" style={{ marginBottom: '20px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div 
              style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: getStatusColor(job.status),
                flexShrink: 0
              }} 
            />
            <div>
              <strong style={{ fontSize: '16px' }}>Status: {job.status}</strong>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                {getStatusDescription(job.status)}
              </p>
              {(job.status === 'FAILED' || job.status === 'ERROR') && (
                <p style={{ margin: '8px 0 0 0', color: '#dc2626', fontSize: '14px', fontWeight: '500' }}>
                  ⚠️ Please contact help@admintools.ai
                </p>
              )}
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
            <div>
              <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Description</div>
              <div>{job.description || 'No description'}</div>
            </div>
            <div>
              <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Files</div>
              <div><strong>{actualUploadedCount}</strong> / {totalFiles} uploaded</div>
            </div>
            {job.csvExtfUrl && (
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>EXTF File</div>
                <a href={job.csvExtfUrl} target="_blank" rel="noreferrer" className="link">
                  Download EXTF
                </a>
              </div>
            )}
            {job.sentToDatevAt && (
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Sent to DATEV</div>
                <div>{new Date(job.sentToDatevAt).toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>

        {/* DATEV Submission Status */}
        {job.status === 'COMPLETED' || job.status === 'PENDING_AT_DATEV' ? (
          <div className="card" style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #10b981' }}>
            <h3 style={{ marginTop: 0, color: '#10b981' }}>DATEV Submission Status</h3>
            {csvSubmission.status && (
              <div style={{ marginBottom: '12px' }}>
                <strong>CSV/EXTF:</strong> 
                <span style={{ 
                  marginLeft: '8px', 
                  padding: '2px 8px', 
                  borderRadius: '4px',
                  backgroundColor: csvSubmission.status === 'submitted' ? '#d1fae5' : '#fee2e2',
                  color: csvSubmission.status === 'submitted' ? '#065f46' : '#991b1b'
                }}>
                  {csvSubmission.status}
                </span>
                {csvSubmission.error && (
                  <div style={{ marginTop: '4px', color: '#dc2626', fontSize: '14px' }}>
                    Error: {typeof csvSubmission.error === 'object' 
                      ? csvSubmission.error.message || JSON.stringify(csvSubmission.error)
                      : csvSubmission.error}
                    <div style={{ marginTop: '8px', fontWeight: '500' }}>
                      ⚠️ Please contact help@admintools.ai
                    </div>
                  </div>
                )}
              </div>
            )}
            {pdfSubmissions.length > 0 && (
              <div>
                <strong>PDFs:</strong> {pdfSubmissions.filter(p => p.status === 'submitted').length} / {pdfSubmissions.length} submitted
              </div>
            )}
          </div>
        ) : null}

        {/* Error Message */}
        {actionError && (
          <div className="alert error" style={{ marginBottom: '20px' }}>
            <strong>Error:</strong> {actionError}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <button 
            onClick={processJob} 
            disabled={processing || !canProcess} 
            className="btn-primary"
          >
            {processing ? 'Processing...' : 'Process Job'}
          </button>
          
          <button 
            onClick={uploadJob} 
            disabled={uploading || !canUpload} 
            className="btn-secondary"
          >
            {uploading ? 'Uploading...' : 'Upload Job to DATEV'}
          </button>
        </div>

        {/* Files Table */}
        <h3>Files ({displayFiles.length})</h3>
        {displayFiles.length > 0 ? (
          <div className="card">
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Filename</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>GUID</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Status</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {displayFiles.map((file, idx) => {
                    const statusColor = file.status === 'UPLOADED' ? '#10b981' : 
                                      file.status === 'FAILED' ? '#ef4444' : 
                                      file.status === 'PENDING' ? '#f59e0b' : '#6b7280';
                    return (
                      <tr key={file.guid || idx}>
                        <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                          {file.filename}
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9', fontFamily: 'monospace', fontSize: '12px' }}>
                          {file.guid}
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            backgroundColor: statusColor + '20',
                            color: statusColor,
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {file.status || 'unknown'}
                          </span>
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}>
                          {file.errorCode || file.errorDescription ? (
                            <div>
                              {file.errorCode && <div><strong>Code:</strong> {file.errorCode}</div>}
                              {file.errorDescription && <div style={{ marginTop: '4px' }}>{file.errorDescription}</div>}
                              {file.errorUri && (
                                <a href={file.errorUri} target="_blank" rel="noopener noreferrer" style={{ marginTop: '4px', display: 'inline-block' }}>
                                  Help
                                </a>
                              )}
                              <div style={{ marginTop: '8px', color: '#dc2626', fontWeight: '500' }}>
                                ⚠️ Please contact help@admintools.ai
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
            No files found for this job.
          </div>
        )}
      </main>
    </div>
  );
};

export default JobDetail;
